import { useMemo } from 'react';
import i18next from 'i18next';

const LOCALE_TO_COUNTRY_LANG: Record<string, { country: string; lang: string }> = {
  en: { country: 'us', lang: 'en' },
  'en-US': { country: 'us', lang: 'en' },
  'en-GB': { country: 'gb', lang: 'en-GB' },
  it: { country: 'it', lang: 'it' },
  'it-IT': { country: 'it', lang: 'it' },
  fr: { country: 'fr', lang: 'fr' },
  'fr-FR': { country: 'fr', lang: 'fr' },
  de: { country: 'de', lang: 'de' },
  'de-DE': { country: 'de', lang: 'de' },
  es: { country: 'es', lang: 'es' },
  'es-ES': { country: 'es', lang: 'es' },
  ja: { country: 'jp', lang: 'ja' },
  'ja-JP': { country: 'jp', lang: 'ja' },
  ko: { country: 'kr', lang: 'ko' },
  'ko-KR': { country: 'kr', lang: 'ko' },
  nl: { country: 'nl', lang: 'nl' },
  'nl-NL': { country: 'nl', lang: 'nl' },
  pt: { country: 'pt', lang: 'pt' },
};

const DEF_COUNTRY = (import.meta.env.VITE_ASSET_DEFAULT_COUNTRY ?? 'us').toLowerCase();
const DEF_LANG = (import.meta.env.VITE_ASSET_DEFAULT_LANG ?? 'en').toLowerCase();

export type AssetLocale = { country: string; lang: string };
export const DEFAULT_ASSET_LOCALE: AssetLocale = { country: DEF_COUNTRY, lang: DEF_LANG };

export function useLocale(): { current: AssetLocale; fallback: AssetLocale } {
  const lng = i18next.language || 'en';
  const supported = i18next.options.supportedLngs as string[] | undefined;
  const fallbackLng = i18next.options.fallbackLng as string | string[] | undefined;

  const fallbackFromI18n = Array.isArray(fallbackLng) ? fallbackLng[0] : fallbackLng || 'en';

  return useMemo(() => {
    const isSupported = (code: string) => !supported || supported.includes(code);

    if (LOCALE_TO_COUNTRY_LANG[lng] && isSupported(lng)) {
      const m = LOCALE_TO_COUNTRY_LANG[lng];
      return { current: m, fallback: DEFAULT_ASSET_LOCALE };
    }

    const short = lng.split('-')[0];
    if (LOCALE_TO_COUNTRY_LANG[short] && isSupported(short)) {
      const m = LOCALE_TO_COUNTRY_LANG[short];
      return { current: m, fallback: DEFAULT_ASSET_LOCALE };
    }

    if (LOCALE_TO_COUNTRY_LANG[fallbackFromI18n]) {
      const m = LOCALE_TO_COUNTRY_LANG[fallbackFromI18n];
      return { current: m, fallback: DEFAULT_ASSET_LOCALE };
    }

    return { current: DEFAULT_ASSET_LOCALE, fallback: DEFAULT_ASSET_LOCALE };
  }, [lng, supported, fallbackFromI18n]);
}
